import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const SECTIONS = [
  {
    id: 'who-we-are',
    title: '1. WHO WE ARE',
    body: [
      'Life Admin Autofill ("we", "us", "our") is an Australian online service, operated under ABN 74 647 107 845, that helps you store your personal, work and financial details once and reuse them to complete job, rental, loan, gym and government forms. This policy explains how we handle your personal information in line with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).',
      'You can use our website (lifeautofill.com), our mobile web app, and our browser extension for Chrome, Edge, Brave and Firefox. This policy covers all of them.',
    ],
  },
  {
    id: 'what-we-collect',
    title: '2. WHAT WE COLLECT',
    list: [
      'Account details: when you sign in with Google we receive your name, email address and profile picture.',
      'Life Profile details you enter or that we extract from your documents: name, date of birth, contact details, addresses, identity document numbers (for example driver licence, passport, Medicare), visa and work-rights status, employment history, income, bank name and account details, tax file number, rental history, references and emergency contacts.',
      'Documents you upload, such as resumes, ID cards, passports, payslips, bank statements, tax records and certificates.',
      'Form Memory: when you correct a field by hand after an autofill, the extension saves the website domain, a technical description of that field (its name, ID or label) and either which profile item belongs there or the value you typed. Passwords, card numbers, CVVs, PINs and one-time codes are never saved.',
      'Usage data: the number of autofills you use, the address of the page where you used the extension, pages you visit on our site, device and browser type, and error reports.',
      'Payment data: Stripe processes all payments. We receive your subscription status and a Stripe customer ID, never your full card number.',
      'Messages you send us through the contact form or by email.',
    ],
  },
  {
    id: 'how-we-use',
    title: '3. HOW WE USE YOUR INFORMATION',
    list: [
      'To build your Life Profile and fill in forms when you ask us to.',
      'To read your uploaded documents with AI and turn them into structured profile data.',
      'To remember how specific websites lay out their forms, so later autofills on those sites are more accurate.',
      'To manage your subscription, enforce plan limits and prevent fraud and abuse.',
      'To fix bugs, improve accuracy and understand which features people use.',
      'To reply to support requests and send essential service notices, such as billing receipts or security alerts.',
    ],
    after: 'We do not sell your personal information. We do not use your documents or profile to train AI models. We do not show you advertising.',
  },
  {
    id: 'ai-processing',
    title: '4. AI DOCUMENT PROCESSING',
    body: [
      'When you click "Parse" on a document, the contents of that single document are sent to Anthropic (Claude) or Google (Gemini) for structured extraction. The result is saved to your private profile. These providers act as our processors, and under their commercial API terms they do not use API inputs to train their models. Nothing from your documents is sent to an AI provider unless you choose to parse a document.',
      'When the browser extension finds a form field it does not recognise, it sends only that field\'s technical description (its label, name and dropdown options, for example "Referee 1 phone") to Google Gemini, which works out which part of your profile belongs there. Your personal values are never included. The answer is stored per website and reused for all users, so the same field is not sent again.',
    ],
  },
  {
    id: 'disclosure',
    title: '5. WHO WE SHARE IT WITH',
    list: [
      'Infrastructure and storage providers that host our application, database and encrypted file storage.',
      'Anthropic and Google, only for documents you choose to parse.',
      'Stripe, for payments and invoices.',
      'Sentry (error monitoring) and PostHog (product analytics). We configure both to keep personal data to a minimum.',
      'Google, for sign-in.',
      'People you choose to share with: when you create a Vault share link, anyone with that link can view the documents and summary you selected until the link expires or you revoke it.',
      'Authorities, where the law requires it.',
    ],
  },
  {
    id: 'overseas',
    title: '6. OVERSEAS DISCLOSURE',
    body: [
      'Some of our service providers store or process data outside Australia, mainly in the United States. Before using them, we take reasonable steps to make sure they protect your information to a standard comparable to the APPs.',
    ],
  },
  {
    id: 'security',
    title: '7. STORAGE & SECURITY',
    body: [
      'Data is encrypted in transit (TLS) and at rest. Documents are kept in private object storage that can only be reached through authenticated requests. Sessions use secure, HTTP-only cookies backed by a server-side session table, and sensitive endpoints are rate-limited. No system is perfectly secure, but if a data breach is likely to cause you serious harm, we will notify you and the Office of the Australian Information Commissioner (OAIC) under the Notifiable Data Breaches scheme.',
    ],
  },
  {
    id: 'retention',
    title: '8. HOW LONG WE KEEP IT',
    body: [
      'We keep your profile, documents and Form Memory for as long as your account is open. Deleted documents are removed from your vault straight away and purged from storage within 30 days. When you delete your account, we remove your profile, documents, applications, share links and Form Memory. We only keep billing records for as long as tax law requires, which is generally 5 years.',
    ],
  },
  {
    id: 'your-rights',
    title: '9. YOUR CHOICES & RIGHTS',
    list: [
      'Access and correction: you can see and edit all of your profile data on the Dashboard at any time.',
      'Deletion: you can delete individual documents, individual Form Memory entries or whole websites from the Extension page. To delete your account, go to Settings → Danger Zone.',
      'Export: you can export any application as JSON, CSV or PDF.',
      'Extension: you can sign out of the extension from its popup at any time, or uninstall it.',
      'Requests: email support@lifeautofill.com and we will respond within 30 days.',
    ],
  },
  {
    id: 'cookies',
    title: '10. COOKIES & LOCAL STORAGE',
    body: [
      'We use a strictly necessary session cookie to keep you signed in, local storage for your theme and sign-in token, and analytics cookies from PostHog to see how the product is used. The extension stores your sign-in token and a cached copy of your autofill data in your browser\'s extension storage, which is cleared when you sign out.',
    ],
  },
  {
    id: 'children',
    title: '11. CHILDREN',
    body: ['Life Admin Autofill is not intended for anyone under 16. We do not knowingly collect information from children.'],
  },
  {
    id: 'complaints',
    title: '12. CHANGES & COMPLAINTS',
    body: [
      'We may update this policy as the product changes. If we make a significant change, we will tell you by email or in the app before it takes effect.',
      'If you have a privacy question or complaint, email support@lifeautofill.com. We aim to respond within 30 days. If you are not satisfied with our response, you can complain to the OAIC at oaic.gov.au or by calling 1300 363 992.',
    ],
  },
];

export default function Privacy() {
  return (
    <div className="container mx-auto px-4 md:px-8 py-12 max-w-3xl" data-testid="privacy-page">
      <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground mb-8">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>
      <h1 className="font-heading text-4xl mb-2">PRIVACY POLICY</h1>
      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Last updated: June 2026</p>
      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-8">Life Admin Autofill · ABN 74 647 107 845 · support@lifeautofill.com</p>

      <p className="text-muted-foreground leading-relaxed mb-8">
        Your Life Profile holds some of the most sensitive information you have. This policy explains, in plain English, what we collect, why we collect it, who can see it, and how to control or delete it.
      </p>

      <nav className="border-2 border-border p-5 mb-10" aria-label="Contents" data-testid="privacy-toc">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">CONTENTS</div>
        <ol className="grid sm:grid-cols-2 gap-1 text-sm">
          {SECTIONS.map((s) => (
            <li key={s.id}><a href={`#${s.id}`} className="hover:text-primary">{s.title.replace(/^\d+\.\s/, '')}</a></li>
          ))}
        </ol>
      </nav>

      <div className="space-y-10 leading-relaxed">
        {SECTIONS.map((s) => (
          <section key={s.id} id={s.id} data-testid={`privacy-section-${s.id}`}>
            <h2 className="font-heading text-sm uppercase tracking-widest text-primary mb-3">{s.title}</h2>
            {s.body?.map((p, i) => <p key={i} className="text-muted-foreground mb-3">{p}</p>)}
            {s.list && (
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                {s.list.map((li, i) => <li key={i}>{li}</li>)}
              </ul>
            )}
            {s.after && <p className="text-foreground font-medium mt-4">{s.after}</p>}
          </section>
        ))}
      </div>
    </div>
  );
}
